import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Snackbar, Alert } from "@mui/material";
// eslint-disable-next-line no-unused-vars
import api, { setTokens, clearTokens, isTokenExpired, refreshAccessToken } from "../services/api";
import { tokenUtils } from "../hooks/useTokenStorage";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const navigate = useNavigate();
  const isDebug = window.env?.REACT_APP_DEBUG === "true" || process.env.REACT_APP_DEBUG === "true";

  useEffect(() => {
    const initializeAuth = async () => {
      const refreshToken = tokenUtils.getRefreshToken();
      if (refreshToken) {
        try {
          if (isDebug) {
            console.log("リフレッシュトークンが存在します。トークンをリフレッシュします");
          }
          
          await refreshAccessToken(api);
          
          const response = await api.get("/api/auth/me");
          if (isDebug) {
            console.log("ユーザー情報取得成功:", response.data);
          }
          setUser(response.data);
        } catch (error) {
          console.error("初期認証エラー:", error);
          clearTokens();
          setUser(null);
        }
      } else {
        if (isDebug) {
          console.log("リフレッシュトークンが存在しません");
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, [isDebug]);

  const login = async (credentials) => {
    try {
      if (isDebug) {
        console.log("[AUTH DEBUG] Login attempt:", {
          timestamp: new Date().toISOString(),
          apiUrl: process.env.REACT_APP_API_URL,
          requestData: {
            ...credentials,
            password: "***",
          },
        });
      }

      const urlParams = new URLSearchParams(window.location.search);
      const isTestMode = urlParams.get('test') === 'true';
      
      const endpoint = isTestMode ? "/api/auth/test-login" : "/api/auth/login";
      
      if (isDebug) {
        console.log("[AUTH DEBUG] Using endpoint:", endpoint, isTestMode ? "(TEST MODE)" : "");
      }

      const response = await api.post(endpoint, credentials);

      if (isDebug) {
        console.log("[AUTH DEBUG] Login response:", {
          timestamp: new Date().toISOString(),
          status: response.status,
          headers: response.headers,
          data: {
            ...response.data,
            accessToken: response.data.accessToken ? "***" : null,
            refreshToken: response.data.refreshToken ? "***" : null,
          },
        });
      }

      const { accessToken, refreshToken, expiresIn, user } = response.data;
      
      setTokens({
        accessToken,
        refreshToken,
        expiresIn
      });
      
      setUser(user);

      if (isDebug) {
        console.log("ユーザー情報:", {
          role: user.role,
          storeId: user.storeId,
          companyId: user.companyId,
          brandId: user.brandId,
        });
      }

      // ユーザーの役割に応じてリダイレクト先を決定
      let redirectPath = "/";
      switch (user.role) {
        case "SYSTEM_ADMIN":
          redirectPath = "/system-admin/dashboard";
          break;
        case "COMPANY_ADMIN":
          redirectPath = "/company-admin/dashboard";
          break;
        case "BRAND_ADMIN":
          redirectPath = "/brand-admin/dashboard";
          break;
        case "STORE_ADMIN":
          if (user.storeId) {
            // STORE_ADMINのデフォルト画面設定を確認
            const defaultScreen = localStorage.getItem("pos_store_admin_default_screen");
            if (defaultScreen === "pos") {
              redirectPath = `/pos/${user.storeId}`;
            } else {
              redirectPath = `/store-admin/${user.storeId}/dashboard`;
            }
          } else {
            throw new Error("店舗情報が見つかりません");
          }
          break;
        case "STORE_STAFF":
          if (user.storeId) {
            redirectPath = `/pos/${user.storeId}`;
          } else {
            throw new Error("店舗情報が見つかりません");
          }
          break;
        default:
          throw new Error("不明なユーザーロール");
      }

      // リダイレクト前にログイン成功メッセージを表示
      setSnackbar({
        open: true,
        message: "ログインに成功しました",
        severity: "success",
      });

      // リダイレクトを確実に実行
      if (isDebug) {
        console.log("リダイレクト先:", redirectPath);
      }
      // リダイレクト前に少し待機し、メッセージが表示されるのを待つ
      await new Promise((resolve) => setTimeout(resolve, 1000));
      navigate(redirectPath, { replace: true });

      return { success: true };
    } catch (error) {
      console.error("[AUTH DEBUG] Login error:", {
        timestamp: new Date().toISOString(),
        name: error.name,
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          headers: error.config?.headers,
        },
      });
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "ログインに失敗しました",
        severity: "error",
      });
      return { success: false, error };
    }
  };

  const logout = async () => {
    try {
      const refreshToken = tokenUtils.getRefreshToken();

      await api.post("/api/auth/logout", { refreshToken });
      
      clearTokens();
      setUser(null);
      navigate("/login");
    } catch (error) {
      console.error("ログアウトエラー:", error);
      clearTokens();
      setUser(null);
      navigate("/login");
    }
  };

  const logoutAll = async () => {
    try {
      await api.post("/api/auth/logout-all");
      clearTokens();
      setUser(null);
      navigate("/login");
      
      setSnackbar({
        open: true,
        message: "全てのデバイスからログアウトしました",
        severity: "success",
      });
    } catch (error) {
      console.error("全デバイスログアウトエラー:", error);
      clearTokens();
      setUser(null);
      navigate("/login");
    }
  };

  const getCurrentUser = async () => {
    try {
      const response = await api.get("/api/auth/me");
      setUser(response.data);
    } catch (error) {
      console.error("ユーザー情報取得エラー:", error);
      if (error.response?.status === 401) {
        clearTokens();
        setUser(null);
        navigate("/login");
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const value = {
    user,
    login,
    logout,
    logoutAll,
    loading,
    getCurrentUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? <div>Loading...</div> : children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
